import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "@fluentui/react-components";
import ResponderForm, { ResponderFormData, initialResponderFormData } from "#root/pages/Merrge211/ResponderForm";
import { showToast } from "#root/components/GlobalToaster";
import { ROUTES } from "#root/routes/constants";
import { useResponderById, useUpdateResponder } from "#root/queries/responders";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { UploaderFile } from "#root/components/ImageUploader";
import { urlToFile } from "#root/services/utils";
import { RESPONDER_STATUS } from "#root/utils/responders";
import { Button } from "@fluentui/react-components";
import { Checkmark24Filled, DocumentText24Filled } from "@fluentui/react-icons";
import { useBadgePrinting } from "#root/hooks/useBadgePrinting";

const CheckInResponder: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState<ResponderFormData>({
        ...initialResponderFormData
    });

    const { printBadges, isPrinting } = useBadgePrinting();

    const searchParams = new URLSearchParams(location.search);
    const responderId = searchParams.get('responderId');

    const updateResponder = useUpdateResponder();

    useEffect(() => {
        if (!responderId) {
            navigate(ROUTES.ERROR_404);
            return;
        }
    }, [responderId, navigate]);

    // Fetch responder data
    const { data: responderResponse, isLoading: responderLoading } = useResponderById({ id: responderId! });

    const responder = responderResponse?.item;

    // Process media files and update form data when responder data is loaded
    useEffect(() => {
        const processResponderData = async () => {
            if (responder) {
                setIsLoading(true);

                // Process images if they exist
                let personalImage: UploaderFile | undefined = undefined;
                let licenseImage: UploaderFile | undefined = undefined;

                if (responder.personalImageUrl) {
                    try {
                        personalImage = await urlToFile(responder.personalImageUrl);
                    } catch (error) {
                        console.error("Error converting personal image:", error);
                    }
                }

                if (responder.licenseImageUrl) {
                    try {
                        licenseImage = await urlToFile(responder.licenseImageUrl);
                    } catch (error) {
                        console.error("Error converting license image:", error);
                    }
                }

                setFormData({
                    ...responder,
                    personalImage,
                    licenseImage
                });
                setIsLoading(false);
            }
        };

        processResponderData();
    }, [responderResponse]);

    const handleFieldChange = (name: string, value: any) => {
        // Only allow changes to the editable fields
        if (name === "checkInDate" || name === "firstDayOfWork" ||
            name === "anticipatedLastDayOfWork" || name === "checkOutDate" ||
            name === "locationId") {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);

            await updateResponder.mutateAsync({
                responder: {
                    ...formData,
                    id: responderId!,
                    status: RESPONDER_STATUS.CHECKED_IN,
                    checkInDate: formData.checkInDate ?? new Date() // Update check in date
                }
            });

            showToast("Responder checked in successfully", "success");
            navigate(ROUTES.MERRGE211.APPROVED_RESPONDERS);
        } catch (error) {
            console.error("Error checking in responder:", error);
            showToast("Failed to check in responder", "error");
        }
        finally {
            setIsSubmitting(false);
        }
    };

    const handlePrintBadge = async () => {
        if (!responderId) return;
        try {
            await printBadges([responderId]);
        } catch (error) {
            showToast("Failed to print badge", "error");
        }
    };

    if (responderLoading || isLoading) {
        return <Spinner size="large" />;
    }

    return (
        <div className="check-in-responder-page">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs />
                    </div>
                </div>
            </div>

            <ResponderForm
                formData={formData}
                onFieldChange={handleFieldChange}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                isCheckIn={true}
                incidentName={responder?.incidentName}
                incidentId={responder?.incidentId}
                actions={
                    <div className="form-actions">
                        <Button
                            size="large"
                            appearance="primary"
                            onClick={handlePrintBadge}
                            disabled={isSubmitting || isPrinting}
                            icon={isPrinting ? <Spinner size="small" /> : <DocumentText24Filled />}
                        >
                            {isPrinting ? "Printing..." : "Print Badge"}
                        </Button>
                        <Button
                            size="large"
                            appearance="primary"
                            className="button-green"
                            type="submit"
                            disabled={isSubmitting}
                            icon={<Checkmark24Filled />}
                        >
                            {isSubmitting ? "Submitting..." : "Check In"}
                        </Button>
                        <Button
                            size="large"
                            disabled={isSubmitting}
                            onClick={() => navigate(ROUTES.MERRGE211.APPROVED_RESPONDERS)}
                        >
                            Cancel
                        </Button>
                    </div>
                }
            />
        </div>
    );
};

export default CheckInResponder;